<template>
  <v-text-field
    v-model="input"
    :error-messages="Array.isArray(error) ? error : []"
    :maxlength="maxLength ? maxLength + Math.floor(maxLength / 3) : 13"
    :clear-icon="clearIcon"
    :messages="priceString"
    :height="40"
    :dense="dense"
    :append-icon="currencyIcon"
    class="centered-input"
    outlined
    :lazy-rules="lazyRules"
    :label="label ?? null"
    v-bind="$attrs"
    @input="handleInput"
    @keypress="isNumberKey"
    :clearable="clearable ?? true"
    :rules="combinedRules"
  ></v-text-field>
</template>

<script>
export default {
  name: "PriceInput",
 inject: {
    getMenuCurrency: {
      default: () => () => 'ریال' 
    }
  },
  props: [
    "value",
    "error",
    "textCenter",
    "maxLength",
    "dense",
    "clearable",
    "label",
    "type",
    "lazyRules",
    "rules",
  ],

  model: {
    prop: "value",
    event: "priceInput",
  },

  data: function () {
    return {
      input: this.convertToHumanReadablePrice(this.value ?? 0),
    };
  },

  created: function () {
    this.$emit("priceInput", this.convertToRealPrice(this.value ?? 0));
  },

  mounted() {
    this.input = this.convertToHumanReadablePrice(this.value ?? 0);
  },

  watch: {
    "$i18n.locale": {
      handler() {
        this.input = this.value
          ? this.convertToHumanReadablePrice(this.value)
          : "";
        this.$forceUpdate();
      },
      immediate: true,
      deep: true,
    },
    value: function (val) {
      if (val?.toString().length > (this.maxLength ?? 10)) {
        let fix = "9".repeat(this.maxLength ?? 10);
        this.$emit("priceInput", this.convertToRealPrice(fix));
        return;
      }
      this.input = this.convertToHumanReadablePrice(this.value ?? 0);
      if ((this.value + "").length > 10) {
        let fixPrice = this.convertToRealPrice(
          this.value.toString().substring(0, 10),
        );
        this.$emit("priceInput", fixPrice);
        this.input = this.convertToHumanReadablePrice(fixPrice ?? 0);
      }
    },
  },

  computed: {
     currencyUnit() {
      return this.getMenuCurrency();
    },
  currencyIcon() {
      switch (this.$i18n.locale) {
        case "fa": return this.currencyUnit === 'ریال' ? 'mdi-currency-rial' : 'mdi-cash';
        case "en": return "mdi-currency-usd";
        case "ar": return "mdi-currency-iqd";
        default: return "mdi-currency-usd";
      }
    },
    currencyLabel() {
      const targetRoutes = ['qr.menu', 'qr.name.editMenu', 'qr.name.id.editMenu'];
      
      if (this.$route && targetRoutes.includes(this.$route.name) && this.currencyUnit) {
        return this.currencyUnit; 
      }

      switch (this.$i18n.locale) {
        case "fa": return "ریال";
        case "en": return "Dollar";
        case "ar": return "دينار";
        default: return "Dollar";
      }
    },
    
    clearIcon() {
      return "mdi-close-circle-outline";
    },
    priceString: function () {
      if (this.input == null || this.input === "") return "";
      const realPrice = this.convertToRealPrice(this.input);
      let words = this.numberToWords(realPrice, this.$i18n.locale);
      return `${words} ${this.currencyLabel}`;
    },
    combinedRules() {
      const parentRules = this.rules || [];
      const localRules = [
        (v) => /^[۰-۹٠-٩0-9,]+$/.test(v || "") || this.$t("invalid_number"),
        (v) => {
          const cleanedValue = this.english((v || "").replace(/,/g, ""));
          const numericValue = parseFloat(cleanedValue) || 0;
          return numericValue >= 0 || this.$t("positive_number");
        },
      ];
      return [...localRules, ...parentRules];
    },
  },

  methods: {
    convertToHumanReadablePrice(input) {
      if (input === null || input === undefined) return "";
      let cleanedInput = this.english(input.toString()).replace(/,/g, "");
      let formatted = cleanedInput.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");

      if (this.$i18n.locale === "fa" || this.$i18n.locale === "ar") {
        formatted = this.toLocalizedDigits(formatted, this.$i18n.locale);
      }
      return formatted;
    },

    convertToRealPrice(input) {
      if (!input) return 0;
      const clean = this.english(input.toString().replace(/,/g, ""));
      return parseInt(clean) || 0;
    },

    handleInput(e) {
      this.$emit("priceInput", this.convertToRealPrice(e));
    },

    isNumberKey: function (evt) {
      const persianChars = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
      const arabicChars = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
      evt = evt ? evt : window.event;
      const charCode = evt.which ? evt.which : evt.keyCode;
      const key = evt.key;

      if (charCode <= 31) return true;

      const isEnglishDigit = charCode >= 48 && charCode <= 57;
      const isPersianDigit = persianChars.includes(key);
      const isArabicDigit = arabicChars.includes(key);

      if (!isEnglishDigit && !isPersianDigit && !isArabicDigit) {
        evt.preventDefault();
      } else {
        return true;
      }
    },

    english(str) {
      const pDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
      const aDigits = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
      return str.replace(/[۰-۹٠-٩]/g, (w) => {
        const pIdx = pDigits.indexOf(w);
        if (pIdx !== -1) return pIdx;
        const aIdx = aDigits.indexOf(w);
        if (aIdx !== -1) return aIdx;
        return w;
      });
    },

    toLocalizedDigits(str, locale) {
      const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
      const arabicDigits = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
      const digits = locale === "ar" ? arabicDigits : persianDigits;
      return str.replace(/[0-9]/g, (d) => digits[parseInt(d)]);
    },

    numberToWords(number, locale) {
      if (number === 0) {
        return locale === "en" ? "zero" : "صفر";
      }

      const langs = {
        fa: {
          units: ["", "یک", "دو", "سه", "چهار", "پنج", "شش", "هفت", "هشت", "نه"],
          teens: ["ده", "یازده", "دوازده", "سیزده", "چهارده", "پانزده", "شانزده", "هفده", "هجده", "نوزده"],
          tens: ["", "", "بیست", "سی", "چهل", "پنجاه", "شصت", "هفتاد", "هشتاد", "نود"],
          hundreds: ["", "صد", "دویست", "سیصد", "چهارصد", "پانصد", "ششصد", "هفتصد", "هشتصد", "نهصد"],
          thousands: ["", "هزار", "میلیون", "میلیارد", "تریلیون"],
          and: " و ",
        },
        en: {
          units: ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"],
          teens: ["ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"],
          tens: ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"],
          hundreds: ["", "one hundred", "two hundred", "three hundred", "four hundred", "five hundred", "six hundred", "seven hundred", "eight hundred", "nine hundred"],
          thousands: ["", "thousand", "million", "billion", "trillion"],
          and: " ",
        },
        ar: {
          units: ["", "واحد", "اثنان", "ثلاثة", "أربعة", "خمسة", "ستة", "سبعة", "ثمانية", "تسعة"],
          teens: ["عشرة", "أحد عشر", "اثنا عشر", "ثلاثة عشر", "أربعة عشر", "خمسة عشر", "ستة عشر", "سبعة عشر", "ثمانية عشر", "تسعة عشر"],
          tens: ["", "", "عشرون", "ثلاثون", "أربعون", "خمسون", "ستون", "سبعون", "ثمانون", "تسعون"],
          hundreds: ["", "مائة", "مائتان", "ثلاثمائة", "أربعمائة", "خمسمائة", "ستمائة", "سبعمائة", "ثمانمائة", "تسعمائة"],
          thousands: ["", "ألف", "مليون", "مليار", "تريليون"],
          and: " و ",
        },
      };

      const lang = langs[locale] || langs["fa"];

      const convertChunk = (num) => {
        let parts = [];
        let h = Math.floor(num / 100);
        let rem = num % 100;

        if (h > 0) parts.push(lang.hundreds[h]);

        if (rem > 0) {
          if (rem < 10) {
            parts.push(lang.units[rem]);
          } else if (rem < 20) {
            parts.push(lang.teens[rem - 10]);
          } else {
            let t = Math.floor(rem / 10);
            let u = rem % 10;
            if (locale === "ar" && u > 0) {
              parts.push(lang.units[u] + lang.and + lang.tens[t]);
            } else {
              parts.push(lang.tens[t]);
              if (u > 0) parts.push(lang.units[u]);
            }
          }
        }
        return parts.join(locale === "en" ? " " : lang.and);
      };

      let parts = [];
      let magnitude = 0;
      let tempNum = number;

      while (tempNum > 0) {
        let chunk = tempNum % 1000;
        if (chunk > 0) {
          let chunkStr = convertChunk(chunk);
          if (magnitude > 0) {
            chunkStr += " " + lang.thousands[magnitude];
          }
          parts.unshift(chunkStr);
        }
        tempNum = Math.floor(tempNum / 1000);
        magnitude++;
      }

      return parts.join(lang.and);
    },
  },
};
</script>

<style scoped>
.centered-input >>> input {
  text-align: center;
}
</style>
